// src/pages/invoices/Invoices.js - VERSIÓN MEJORADA CON ESTILO NEGRO
import React, { useState, useMemo } from 'react';
import {
    Box,
    Button,
    Typography,
    Snackbar,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    IconButton,
    CircularProgress,
    LinearProgress,
    styled
} from '@mui/material';
import {
    Add as AddIcon,
    Close as CloseIcon,
    DeleteForever as DeleteIcon,
    Cancel as CancelIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { InvoiceTable } from './InvoiceTable';
import UnifiedDocumentForm from '../documents/UnifiedDocumentForm';
import { invoicesApi } from '../../services/api';
import NotaAjusteDialog from './components/NotaAjusteDialog';
import RetencionDialog from './components/RetencionDialog';
import { tasaDeItem } from '../../config/impuestos';
import { InvoicePreview } from './InvoicePreview/InvoicePreview';
import { useInvoices } from '../../hooks/useInvoices';
import { useClients } from '../../hooks/useClients';
import { useProducts } from '../../hooks/useProducts';
import { generatePDF } from '../../utils/pdfGenerator';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { ActionButton as GradientButton } from '../../components/common/ActionButton';

// Componentes estilizados
const PageContainer = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#0A0A0C',
  color: 'white',
  minHeight: 'calc(100vh - 64px)',
  position: 'relative'
}));


const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundImage: 'linear-gradient(to right, #F5A623 0%, #D9890D 100%)',
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 24px',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 'calc(100vh - 64px)',
  backgroundColor: '#0A0A0C'
}));

const Invoices = () => {
  // Obtener permisos del hook
  const { canCreate } = useRoleAccess();

  const [openForm, setOpenForm] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState({ open: false, message: '', severity: 'info' });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  // Anulación: la factura a anular y el motivo, que el servidor exige para
  // dejar constancia de por qué se anuló.
  const [invoiceToAnular, setInvoiceToAnular] = useState(null);
  // La retención se carga después de emitir: el cliente retiene al pagar y el
  // comprobante puede llegar semanas más tarde.
  const [facturaRetencion, setFacturaRetencion] = useState(null);
  const [guardandoRetencion, setGuardandoRetencion] = useState(false);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [anulando, setAnulando] = useState(false);
  const [invoiceParaNota, setInvoiceParaNota] = useState(null);
  const [invoiceIdToDelete, setInvoiceIdToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // El hook expone `refreshInvoices`; pedirle `fetchInvoices` devolvía undefined,
  // y como cada llamada estaba envuelta en `typeof … === 'function'`, el fallo era
  // mudo: la lista no se actualizaba después de borrar o anular y había que
  // recargar la página a mano para ver el cambio.
  const { invoices, loading: loadingInvoices, saveInvoice, deleteInvoice, changeInvoiceStatus, refreshInvoices } = useInvoices();
  const { clients, loading: loadingClients, saveClient, agregarClienteLocal } = useClients();
  const { products, loading: loadingProducts, saveProduct } = useProducts();

  // Alta rápida desde el formulario de factura. El cliente se suma a la lista en
  // memoria en vez de volver a pedirla: fetchClients pone loading en true y esta
  // pantalla devuelve un spinner mientras carga, lo que desmontaba el formulario
  // y borraba la factura a medio cargar.
  const crearClienteRapido = async (datos) => {
    const creado = await saveClient(datos);
    agregarClienteLocal(creado);
    return creado;
  };

  // Ordenar facturas por fecha descendente
  const sortedInvoices = useMemo(() => {
    if (!invoices) return [];
    return [...invoices].sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
    });
  }, [invoices]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbarInfo({ open: true, message, severity });
  };

  // Función para procesar datos
  const processInvoiceData = (invoice) => {
    console.log('Procesando datos de factura:', invoice);
    if (!invoice) return null;
    try {
      const processedItems = invoice.items?.map(item => {
        // El tipo fiscal manda; exentoIva y taxExempt quedan por compatibilidad.
        const taxType = item.taxType || (item.exentoIva === true || item.taxExempt === true ? 'exento' : 'gravado');
        return {
          codigo: item.codigo || item.product?.codigo || '',
          descripcion: item.descripcion || item.product?.nombre || '',
          cantidad: parseFloat(item.quantity || item.cantidad) || 1,
          precioUnitario: parseFloat(item.price || item.precioUnitario) || 0,
          total: parseFloat(item.subtotal) ||
                (parseFloat(item.quantity || item.cantidad) || 1) * (parseFloat(item.price || item.precioUnitario) || 0),
          taxType,
          alicuota: item.alicuota || 'general',
          exentoIva: taxType === 'exento'
        };
      }) || [];

      // Los importes salen de la factura guardada, no de un recálculo acá. El
      // backend ya los resuelve en su pre-save separando gravado, exento y no
      // gravado, y esta pantalla los recalculaba mirando sólo si el ítem estaba
      // exento: un ítem no gravado recibía el 16% y el documento impreso mostraba
      // una base y un IVA distintos a los que registró el sistema.
      const sumaItems = processedItems.reduce((sum, item) => sum + item.total, 0);
      const recalcularIva = () => processedItems.reduce(
        (sum, item) => sum + item.total * tasaDeItem(item.taxType, item.alicuota), 0
      );
      const subtotal = invoice.subtotal ?? sumaItems;
      const iva = invoice.tax ?? recalcularIva();
      const total = invoice.total ?? (subtotal + iva);

      const processedInvoice = {
        ...invoice,
        items: processedItems,
        subtotal, iva, total,
        // Bases por tipo fiscal, para que el documento impreso las muestre tal
        // como quedaron guardadas.
        subtotalGravado: invoice.subtotalGravado ?? processedItems.filter(i => i.taxType === 'gravado').reduce((s, i) => s + i.total, 0),
        subtotalExento: invoice.subtotalExento ?? processedItems.filter(i => i.taxType === 'exento').reduce((s, i) => s + i.total, 0),
        subtotalNoGravado: invoice.subtotalNoGravado ?? processedItems.filter(i => i.taxType === 'no_gravado').reduce((s, i) => s + i.total, 0),
        moneda: invoice.moneda || 'VES'
      };
      console.log('Factura procesada:', processedInvoice);
      return processedInvoice;
    } catch (error) { 
      console.error('Error procesando factura:', error); 
      throw error; 
    }
  };

  const handlePreview = (invoice) => {
    try {
      const processedInvoice = processInvoiceData(invoice);
      setSelectedInvoice(processedInvoice);
      setOpenPreview(true);
    } catch (error) {
      console.error('Error al procesar factura para vista previa:', error);
      showSnackbar('Error al cargar la vista previa: ' + error.message, 'error');
    }
  };

  const handleDownload = async (invoice) => {
    setIsSubmitting(true);
    try {
      const processedInvoice = processInvoiceData(invoice);
      if (!processedInvoice) throw new Error("No se pudo procesar la factura.");
      setSelectedInvoice(processedInvoice);
      showSnackbar('Generando PDF...', 'info');
      await generatePDF(processedInvoice);
    } catch (error) {
      console.error('Error preparando/generando PDF:', error);
      showSnackbar('Error al generar el PDF: ' + error.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (invoice) => {
    console.log('Editando factura:', invoice);
    setSelectedInvoice(invoice);
    setOpenForm(true);
  };

  const handleDelete = (id) => {
    setInvoiceIdToDelete(id);
    setOpenConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (invoiceIdToDelete) {
      setDeleting(true);
      setIsSubmitting(true);
      try {
        await deleteInvoice(invoiceIdToDelete);
        showSnackbar('Factura eliminada exitosamente', 'success');
      } catch (err) {
        console.error('Error al eliminar la factura:', err);
        showSnackbar('Error al eliminar la factura: ' + (err.message || 'Error desconocido'), 'error');
      } finally {
        setDeleting(false);
        setIsSubmitting(false);
        setOpenConfirmDialog(false);
        setInvoiceIdToDelete(null);
        await refreshInvoices();
      }
    }
  };

  const handleGuardarRetencion = async (datos) => {
    if (!facturaRetencion) return;
    setGuardandoRetencion(true);
    try {
      await invoicesApi.registrarRetencion(facturaRetencion._id, datos);
      showSnackbar(
        datos.monto > 0
          ? `Retención registrada en ${facturaRetencion.number || 'la factura'}. El total de la factura no cambia.`
          : `Retención quitada de ${facturaRetencion.number || 'la factura'}.`,
        'success'
      );
      setFacturaRetencion(null);
      await refreshInvoices();
    } catch (e) {
      // Se relanza para que el diálogo muestre el motivo y no se cierre: los
      // mensajes del servidor explican qué corregir.
      throw e;
    } finally {
      setGuardandoRetencion(false);
    }
  };

  const handleAnular = (invoice) => {
    setInvoiceToAnular(invoice);
    setMotivoAnulacion('');
  };

  const handleConfirmAnular = async () => {
    if (!invoiceToAnular || !motivoAnulacion.trim()) return;
    setAnulando(true);
    setIsSubmitting(true);
    try {
      await invoicesApi.anular(invoiceToAnular._id, motivoAnulacion.trim());
      showSnackbar(`Factura ${invoiceToAnular.number || ''} anulada. Conserva su número.`, 'success');
      setInvoiceToAnular(null);
    } catch (err) {
      console.error('Error al anular la factura:', err);
      showSnackbar(err.message || 'No se pudo anular la factura', 'error');
    } finally {
      setAnulando(false);
      setIsSubmitting(false);
      await refreshInvoices();
    }
  };

  const handleEmitirNota = async ({ invoiceId, tipo, motivo, items, restaurarStock }) => {
    const resultado = await invoicesApi.emitirNota(invoiceId, tipo, { motivo, items, restaurarStock });
    showSnackbar(resultado?.message || 'Nota emitida.', 'success');
    await refreshInvoices();
    return resultado;
  };

  const handleCloseConfirmDialog = () => {
    if (deleting) return;
    setOpenConfirmDialog(false);
    setInvoiceIdToDelete(null);
  };

  const handleStatusChange = async (id, newStatus) => {
    setIsSubmitting(true);
    try {
      await changeInvoiceStatus(id, newStatus);
      const statusLabels = { 
        draft: 'Borrador', 
        pending: 'Pendiente', 
        paid: 'Pagada', 
        partial: 'Pago Parcial', 
        overdue: 'Vencida', 
        cancelled: 'Anulada' 
      };
      const statusLabel = statusLabels[newStatus] || newStatus;
      showSnackbar(`Estado de la factura actualizado a "${statusLabel}"`, 'success');
      await refreshInvoices();
    } catch (err) {
      console.error('Error al cambiar el estado de la factura:', err);
      showSnackbar('Error al cambiar el estado: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Indicador de carga principal
  if (loadingInvoices || loadingClients) {
    return (
      <LoadingContainer>
        <CircularProgress sx={{ color: '#F5A623' }} />
        <Typography sx={{ color: 'white', ml: 2 }}>Cargando facturas...</Typography>
      </LoadingContainer>
    );
  }

  return (
    <PageContainer>
      {/* Indicador de carga global para acciones */}
      {isSubmitting && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 1500 }}>
          <LinearProgress 
            sx={{
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(to right, #F5A623 0%, #D9890D 100%)'
              },
              backgroundColor: 'rgba(0,0,0,0.2)'
            }}
          />
        </Box>
      )}

      {/* Botón Nueva Factura */}
      <Box display="flex" justifyContent="flex-end" mb={3}>
        {canCreate() && (
          <GradientButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedInvoice(null);
              setOpenForm(true);
            }}
            disabled={isSubmitting}
          >
            Nueva factura
          </GradientButton>
        )}
      </Box>

      {/* Tabla de facturas con estilo unificado */}
      <Box sx={{ mb: 3 }}>
        <InvoiceTable
          invoices={sortedInvoices}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAnular={handleAnular}
          onRetencion={setFacturaRetencion}
          onNota={setInvoiceParaNota}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onStatusChange={handleStatusChange}
        />
      </Box>

      {/* Modal de Formulario Unificado */}
      <UnifiedDocumentForm
        open={openForm}
        initialData={selectedInvoice}
        clients={clients || []}
        products={products || []}
        onCreateClient={crearClienteRapido}
        onCreateProduct={saveProduct}
        onClose={() => {
          setOpenForm(false);
          setSelectedInvoice(null);
        }}
        onSave={async (data) => {
          setIsSubmitting(true);
          try {
            await saveInvoice(data);
            showSnackbar(data._id ? 'Factura actualizada correctamente' : 'Factura creada correctamente', 'success');
            setOpenForm(false);
            setSelectedInvoice(null);
            await refreshInvoices();
          } catch(err) {
            console.error("Error al guardar desde form:", err);
            showSnackbar('Error al guardar la factura: ' + (err.message || 'Error desconocido'), 'error');
          } finally {
            setIsSubmitting(false);
          }
        }}
        isInvoice={true}
      />

      {/* Modal de Vista Previa */}
      <InvoicePreview
        open={openPreview}
        invoice={selectedInvoice}
        onClose={() => {
          setOpenPreview(false);
          setSelectedInvoice(null);
        }}
      />

      <NotaAjusteDialog
        open={!!invoiceParaNota}
        invoice={invoiceParaNota}
        onClose={() => setInvoiceParaNota(null)}
        onEmitir={handleEmitirNota}
      />

      {/* Diálogo de anulación. Pide el motivo porque queda registrado junto con
          quién anuló y cuándo: es lo que se mira en una fiscalización. */}
      <RetencionDialog
        factura={facturaRetencion}
        open={!!facturaRetencion}
        onClose={() => !guardandoRetencion && setFacturaRetencion(null)}
        onGuardar={handleGuardarRetencion}
        guardando={guardandoRetencion}
      />

      <Dialog
        open={!!invoiceToAnular}
        onClose={() => !anulando && setInvoiceToAnular(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1B1B1F',
            color: 'white',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <StyledDialogTitle>
          Anular factura {invoiceToAnular?.number || ''}
          <IconButton
            onClick={() => setInvoiceToAnular(null)}
            sx={{ color: 'white' }}
            disabled={anulando}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
            Una factura emitida no se elimina: se anula. Conserva su número y su
            número de control, y el correlativo sigue sin huecos — un salto en la
            numeración es lo primero que se revisa en una fiscalización.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={2}
            label="Motivo de la anulación"
            value={motivoAnulacion}
            onChange={(e) => setMotivoAnulacion(e.target.value)}
            disabled={anulando}
            placeholder="Por ejemplo: error en los datos del cliente"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.25)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#F5A623' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => setInvoiceToAnular(null)}
            variant="outlined"
            disabled={anulando}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              fontWeight: 600
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmAnular}
            variant="contained"
            color="error"
            disabled={anulando || !motivoAnulacion.trim()}
            startIcon={anulando ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {anulando ? 'Anulando…' : 'Anular factura'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        disableEscapeKeyDown={deleting}
        PaperProps={{ 
          sx: { 
            backgroundColor: '#1B1B1F', 
            color: 'white', 
            borderRadius: '8px', 
            border: '1px solid rgba(255, 255, 255, 0.1)' 
          } 
        }}
      >
        <StyledDialogTitle>
          Eliminar borrador
          <IconButton 
            onClick={handleCloseConfirmDialog} 
            sx={{ color: 'white' }} 
            disabled={deleting}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
            ¿Seguro que querés eliminar este borrador? No llegó a emitirse, así que
            no deja hueco en la numeración. La acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={handleCloseConfirmDialog}
            variant="outlined"
            startIcon={<CancelIcon />}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              borderColor: 'rgba(255, 255, 255, 0.3)',
              fontWeight: 600,
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.5)'
              } 
            }} 
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained" 
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{ 
              background: 'linear-gradient(to right, #ff416c, #ff4b2b)', 
              boxShadow: '0 4px 15px rgba(255, 75, 43, 0.4)',
              fontWeight: 600
            }} 
            disabled={deleting}
          >
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensajes */}
      <Snackbar
        open={snackbarInfo.open}
        autoHideDuration={6000}
        onClose={() => setSnackbarInfo({ ...snackbarInfo, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarInfo({ ...snackbarInfo, open: false })}
          severity={snackbarInfo.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
            borderRadius: '8px',
            ...(snackbarInfo.severity === 'success' && {
              backgroundImage: 'linear-gradient(to right, #F5A623 0%, #D9890D 100%)',
              '& .MuiAlert-icon': { color: 'white' },
              color: 'white'
            })
          }}
        >
          {snackbarInfo.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Invoices;
